import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  Flex,
  Icon,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Text,
  cn,
} from '@kognitos/lattice';
import { SPRING_EASE_CLASS } from '@/shared/constants/motion';

import type {
  IAssetTemplateBrowserLabels,
  IAssetTemplateCardAction,
  IAssetTemplateFolder,
  IAssetTemplateItem,
} from './AssetTemplateBrowser';

/**
 * Props for {@link AssetTemplateCard}.
 */
export interface IAssetTemplateCardProps<T> {
  item: IAssetTemplateItem<T>;
  /** Opens the template's detail. */
  onOpen: (item: IAssetTemplateItem<T>) => void;
  action?: IAssetTemplateCardAction<T>;
  /** Something is in flight; every card holds still. */
  isBusy?: boolean;
  /** This card's own action is in flight. */
  isPending?: boolean;
  /** Warms what the detail needs, on hover or focus. */
  onPrefetch?: (item: IAssetTemplateItem<T>) => void;
}

/**
 * A template in the gallery: its visual, title, subtitle and description, its
 * badges, and an optional secondary action. Clicking anywhere else opens it.
 */
export function AssetTemplateCard<T>({
  item,
  onOpen,
  action,
  isBusy = false,
  isPending = false,
  onPrefetch,
}: IAssetTemplateCardProps<T>) {
  const id = useId();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [wasBusy, setWasBusy] = useState(isBusy);
  // A busy card ignores the pointer, so a leave in that window never lands; drop hover when busy starts.
  if (isBusy !== wasBusy) {
    setWasBusy(isBusy);
    if (isBusy) setIsHovered(false);
  }
  const isActive = !isBusy && (isHovered || isFocused);
  const describedBy = [
    item.subtitle && `${id}-subtitle`,
    item.description && `${id}-description`,
  ]
    .filter(Boolean)
    .join(' ');
  const open = () => {
    if (!isBusy) onOpen(item);
  };
  const runAction = () => {
    if (!isBusy) action?.onClick(item);
  };

  // The open control is an overlay, not the card, so the action button isn't
  // nested inside another button. Content paints above it but lets clicks through.
  return (
    <Card
      onPointerEnter={() => {
        setIsHovered(true);
        onPrefetch?.(item);
      }}
      onPointerMove={() => {
        if (!isHovered) setIsHovered(true);
      }}
      onPointerLeave={() => setIsHovered(false)}
      onFocus={() => {
        setIsFocused(true);
        onPrefetch?.(item);
      }}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsFocused(false);
        }
      }}
      className={cn(
        'relative flex w-full min-w-0 flex-col gap-3 overflow-hidden p-4 transition-colors hover:border-ring/60 hover:bg-muted/40',
        isBusy && 'pointer-events-none opacity-50',
      )}
      data-testid={`asset-template-card-${item.id}`}
    >
      <Button
        variant="ghost"
        aria-label={item.title}
        aria-describedby={describedBy || undefined}
        disabled={isBusy}
        onClick={open}
        className="absolute inset-0 size-auto rounded-[inherit] p-0 disabled:opacity-100 not-disabled:hover:bg-transparent dark:not-disabled:hover:bg-transparent"
        data-testid={`asset-template-card-${item.id}-open`}
      />
      <Flex
        align="center"
        gap={3}
        width="full"
        className="pointer-events-none relative min-w-0"
      >
        {item.media && (
          <Flex
            className={cn(
              'shrink-0 transition-transform duration-300',
              SPRING_EASE_CLASS,
              isActive && 'motion-safe:-translate-y-0.5 motion-safe:scale-105',
            )}
          >
            {item.media}
          </Flex>
        )}
        <Flex
          direction="column"
          align="stretch"
          gap={0}
          className="min-w-0 flex-1"
        >
          <Text weight="medium" ellipsis>
            {item.title}
          </Text>
          {item.subtitle && (
            <Text
              id={`${id}-subtitle`}
              level="xSmall"
              color="muted"
              className="truncate"
            >
              {item.subtitle}
            </Text>
          )}
        </Flex>
      </Flex>

      {item.description && (
        <Text
          id={`${id}-description`}
          level="small"
          color="muted"
          className="pointer-events-none relative line-clamp-2 break-words leading-relaxed"
        >
          {item.description}
        </Text>
      )}

      {(item.badges?.length || action) && (
        <Flex
          align="center"
          justify="between"
          gap={2}
          width="full"
          className="pointer-events-none relative mt-auto border-t pt-3"
        >
          <Flex align="center" gap={1.5} className="min-w-0">
            {item.badges?.map(badge => (
              <Text
                key={badge.label}
                as="span"
                level="small"
                color="muted"
                className="tabular-nums"
              >
                {badge.label}
              </Text>
            ))}
          </Flex>
          {action && (
            <Button
              // Primary while the card is hovered or focused.
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              onClick={runAction}
              className="pointer-events-auto"
              isLoading={isPending}
              disabled={isBusy}
              aria-label={action.ariaLabel?.(item.title)}
              data-testid={`asset-template-card-${item.id}-action`}
            >
              {action.label}
              {action.trailingIcon && !isPending && (
                <Icon type={action.trailingIcon} size="xs" />
              )}
            </Button>
          )}
        </Flex>
      )}
    </Card>
  );
}

interface IAssetTemplateGroupPickerProps<T> {
  folders: IAssetTemplateFolder<T>[];
  labels: IAssetTemplateBrowserLabels;
  /** `null` for every template; `undefined` while a search shows no selection. */
  selectedGroup: string | null | undefined;
  totalCount: number;
  onSelectGroup: (groupId: string | null) => void;
}

const ALL_GROUPS = '__all__';

/**
 * The rail's groups as a select, for widths where the rail is hidden.
 */
export function AssetTemplateGroupSelect<T>({
  folders,
  labels,
  selectedGroup,
  totalCount,
  onSelectGroup,
}: IAssetTemplateGroupPickerProps<T>) {
  const { t } = useTranslation('common');
  const allLabel = labels.allItems ?? t('common.assetTemplates.all');
  return (
    <Select
      // Empty while searching, so picking "All" is a change and clears the search.
      value={selectedGroup === undefined ? '' : (selectedGroup ?? ALL_GROUPS)}
      onValueChange={value =>
        onSelectGroup(value === ALL_GROUPS ? null : value)
      }
    >
      <SelectTrigger
        aria-label={labels.railLabel ?? labels.title}
        className="mb-3 w-full md:hidden"
        data-testid="asset-template-group-select"
      >
        <SelectValue placeholder={t('common.assetTemplates.searchResults')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_GROUPS}>
          {allLabel} ({totalCount})
        </SelectItem>
        {folders.map(folder => (
          <SelectGroup key={folder.id}>
            <SelectLabel>{folder.label}</SelectLabel>
            {folder.groups.map(group => (
              <SelectItem key={group.id} value={group.id}>
                {group.label} ({group.items.length})
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The gallery's rail: every template, then each folder as a heading over its
 * groups, with counts. A search clears the selection, since results span
 * every group.
 */
export function AssetTemplateRail<T>({
  folders,
  labels,
  selectedGroup,
  totalCount,
  onSelectGroup,
}: IAssetTemplateGroupPickerProps<T>) {
  const { t } = useTranslation('common');
  return (
    <Flex
      asChild
      direction="column"
      align="stretch"
      gap={0.5}
      className="hidden w-56 shrink-0 overflow-y-auto border-r px-3 py-4 md:flex"
    >
      <nav
        aria-label={labels.railLabel ?? labels.title}
        data-testid="asset-template-rail"
      >
        <RailButton
          label={labels.allItems ?? t('common.assetTemplates.all')}
          count={totalCount}
          isCurrent={selectedGroup === null}
          onClick={() => onSelectGroup(null)}
          data-testid="asset-template-rail-all"
        />
        {folders.map(folder => (
          <Flex key={folder.id} direction="column" align="stretch" gap={0.5}>
            <Text type="eyebrow" className="mt-3 mb-1.5 px-3">
              {folder.label}
            </Text>
            {folder.groups.map(group => (
              <RailButton
                key={group.id}
                label={group.label}
                count={group.items.length}
                isCurrent={selectedGroup === group.id}
                onClick={() => onSelectGroup(group.id)}
                data-testid={`asset-template-rail-${group.id}`}
              />
            ))}
          </Flex>
        ))}
      </nav>
    </Flex>
  );
}

function RailButton({
  label,
  count,
  isCurrent,
  onClick,
  'data-testid': dataTestId,
}: {
  label: string;
  count: number;
  isCurrent: boolean;
  onClick: () => void;
  'data-testid'?: string;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-current={isCurrent}
      className="w-full justify-between font-normal aria-[current=true]:bg-muted aria-[current=true]:font-medium"
      onClick={onClick}
      data-testid={dataTestId}
    >
      <span className="truncate">{label}</span>
      <span className="tabular-nums text-muted-foreground">{count}</span>
    </Button>
  );
}
